import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Building2, Menu, X, ShieldCheck, LogIn, CheckCircle2, ChevronDown } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import NotificationBell from "./NotificationBell";

const DEFAULT_LINKS = [
  { type: "link", to: "/", label: "Start", key: "start" },
  { type: "link", to: "/news", label: "News", key: "news" },
  { type: "link", to: "/historie", label: "Historie", key: "historie" },
  { type: "link", to: "/events", label: "Events", key: "events" },
  { type: "link", to: "/projekte", label: "Projekte", key: "projekte" },
  { type: "link", to: "/badges", label: "Badges", key: "badges" },
  { type: "link", to: "/sammlung", label: "Sammlung", key: "sammlung" },
  { type: "link", to: "/shop", label: "Shop", key: "shop" },
  { type: "link", to: "/team", label: "Team", key: "team" },
  { type: "link", to: "/jobs", label: "Jobs", key: "jobs" },
  { type: "link", to: "/support", label: "Support", key: "support" },
];

const avatarHead = (name) =>
  `https://www.habbo.de/habbo-imaging/avatarimage?user=${encodeURIComponent(name)}&direction=2&head_direction=3&size=b&action=std&gesture=sml&headonly=1`;

const NavDropdown = ({ item, location }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const anyActive = (item.children || []).some((c) => c.to === location.pathname);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={`nav-dropdown-${(item.label || "").toLowerCase()}`}
        onClick={() => setOpen((v) => !v)}
        className={`nav-link inline-flex items-center gap-1 text-sm font-medium ${anyActive ? "text-white" : "text-zinc-300 hover:text-white"}`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-xl border border-white/10 bg-[#0f141b] shadow-2xl">
          {(item.children || []).map((c, i) => (
            <Link
              key={i}
              to={c.to}
              onClick={() => setOpen(false)}
              className={`block px-3 py-2 text-sm transition-colors ${location.pathname === c.to ? "bg-white/10 text-white" : "text-zinc-300 hover:bg-white/5 hover:text-white"}`}
            >{c.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState([]);
  const [navItems, setNavItems] = useState(null); // null = use default
  const { user, userToken, adminToken } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const s = await api.getSettings();
        setHidden(s.hidden_pages || []);
        const items = Array.isArray(s.nav_items) ? s.nav_items : [];
        setNavItems(items.length > 0 ? items : null);
      } catch { /* ignore */ }
    })();
  }, []);

  // Schreibtisch link Review-Count Notification
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    if (!userToken && !adminToken) return;
    const token = adminToken || userToken;
    const userCanReview = !!adminToken || !!(user && user.permissions && user.permissions.news_review);
    if (!userCanReview) return;
    const fetchCount = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/review-count`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          setReviewCount(d.count || 0);
        }
      } catch { /* ignore */ }
    };
    fetchCount();
    const i = setInterval(fetchCount, 60 * 1000);
    return () => clearInterval(i);
  }, [adminToken, userToken, user]);

  const visibleLinks = (navItems || DEFAULT_LINKS).filter((l) => !l.key || !hidden.includes(l.key));
  const userHasAdminAccess = !!(user && user.permissions && user.permissions.admin_access);
  const userCanWrite = !!(user && user.permissions && (user.permissions.news_write || user.permissions.news_review || user.permissions.events_edit || user.permissions.projects_edit || user.permissions.badges_edit || user.permissions.rares_edit || user.permissions.furniture_edit));
  const canSeeAdmin = !!adminToken || userHasAdminAccess || userCanWrite;

  return (
    <header className="sticky top-3 z-40 mx-auto max-w-7xl px-4">
      <nav className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#0f141b]/85 px-4 py-3 backdrop-blur-md">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#7c4dff] to-[#ff9800]">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <span className="pixel-text text-sm tracking-wider text-white">HABTHEK</span>
        </Link>

        <div className="hidden items-center gap-6 lg:flex">
          {visibleLinks.map((l, idx) => {
            if (l.type === "dropdown") {
              return <NavDropdown key={idx} item={l} location={location} />;
            }
            const active = location.pathname === l.to;
            return (
              <Link
                key={idx}
                to={l.to}
                data-testid={`nav-link-${l.key || (l.label || "").toLowerCase()}`}
                className={`nav-link text-sm font-medium ${active ? "active text-white" : "text-zinc-300 hover:text-white"}`}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden md:flex items-center gap-2">
          <NotificationBell />
          {userToken && user ? (
            <Link to="/profil" data-testid="navbar-user-link" className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-sm text-zinc-200 hover:border-white/20">
              <img src={avatarHead(user.habbo_name)} alt="" className="hb-pixel-img h-7" />
              <span className="font-semibold">{user.habbo_name}</span>
              {user.verified && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
            </Link>
          ) : (
            <Link to="/login" data-testid="navbar-login-link" className="btn-ghost inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold">
              <LogIn className="h-4 w-4" /> Login
            </Link>
          )}
          {canSeeAdmin ? (
            <Link to="/admin" data-testid="navbar-admin-link" className="btn-primary inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold relative">
              <ShieldCheck className="h-4 w-4" /> Admin
              {reviewCount > 0 && (
                <span data-testid="navbar-review-count" className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white shadow-lg shadow-rose-500/40">
                  {reviewCount}
                </span>
              )}
            </Link>
          ) : (
            <Link to="/admin/login" data-testid="navbar-admin-login" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 text-sm font-semibold text-zinc-300 hover:text-white">
              <ShieldCheck className="h-4 w-4" />
            </Link>
          )}
        </div>

        <button
          className="lg:hidden rounded-lg p-2 text-zinc-200 hover:bg-white/5"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menü"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden mt-2 rounded-2xl border border-white/10 bg-[#0f141b]/95 p-3">
          {visibleLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block rounded-lg px-3 py-2 text-sm text-zinc-200 hover:bg-white/5"
            >
              {l.label}
            </Link>
          ))}
          <div className="mt-2 grid grid-cols-2 gap-2">
            {userToken ? (
              <Link to="/profil" onClick={() => setOpen(false)} className="btn-ghost rounded-lg px-3 py-2 text-sm text-center">Profil</Link>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost rounded-lg px-3 py-2 text-sm text-center">Login</Link>
            )}
            {canSeeAdmin && (
              <Link to="/admin" onClick={() => setOpen(false)} className="btn-primary rounded-lg px-3 py-2 text-sm text-center font-semibold">Admin</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
